/**
 * Detects errors that mean a lazy-loaded chunk could not be fetched.
 * This happens to returning users whose still-running (stale) bundle points at
 * hashed `/assets/*` chunks that a newer deploy has already removed. The symptom
 * is a blank page, and it is NOT recoverable with a client-side navigation
 * (every other lazy chunk comes from the same dead bundle).
 */
export function isStaleChunkError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err ?? '')
    return (
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||
        /Unable to preload CSS/i.test(message) ||
        /ChunkLoadError/i.test(message) ||
        // Safari surfaces this as a generic module/script load failure.
        /'?text\/html'? is not a valid JavaScript MIME type/i.test(message)
    )
}

const RELOAD_GUARD_KEY = 'lh:chunk-reload-at'
// If we already force-reloaded very recently, don't do it again — that would
// be a genuine failure (offline, asset truly missing) rather than a stale
// bundle, and we must avoid an infinite reload loop.
const RELOAD_GUARD_WINDOW_MS = 10_000

/**
 * Full reload onto `target`, once per guard window. Returns false when a
 * reload already happened moments ago, so the caller should show an error
 * instead of looping.
 */
export function reloadForStaleChunk(target?: string): boolean {
    const now = Date.now()
    let lastReloadAt = 0
    try {
        lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)
    } catch {
        lastReloadAt = 0
    }
    if (now - lastReloadAt <= RELOAD_GUARD_WINDOW_MS) return false
    try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now))
    } catch {
        // sessionStorage unavailable (private mode / blocked) — reload anyway.
    }
    // Full reload pulls the fresh (no-cache) index.html and its current
    // chunk hashes, landing the user on the page they intended to open.
    window.location.assign(target ?? window.location.pathname + window.location.search + window.location.hash)
    return true
}
